import React, { MouseEventHandler, useEffect, useRef, useState } from 'react';
import styles from './ContactFormGeneric.module.scss';
import { Button, Title, Group, RadioGroup, Radio, Textarea, Select, TextInput, LoadingOverlay, Divider, Space, Text, Alert  } from '@mantine/core';
import { useDispatch, useSelector } from 'react-redux';
import { useDebouncedCallback } from "use-debounce";
import { store } from '../../redux/store';
import { appActions, selectContactState, selectCurrentEmail, selectCountry, selectFrontlineCountries, roles, titles, usStates, caTerritories, countryList, selectDomainCountry } from '../../redux/appSlice';
import chevronDown from '../../assets/icons/chevron-down.svg'
import useStyles from '../../hooks/useStyles';
import useTranslation from '../../hooks/useTranslation';
import { sendFormGeneric } from '../../services/api'
import { useForm } from '@mantine/form';
import {sortCountries} from '../../utils/general-utils'
import { InfoCircleRed } from '../icons/Icons';


type Props = {
  executeRecaptcha: any,
  contactType:string,
  children?: React.ReactNode,  
}

function ContactFormGeneric({ executeRecaptcha, contactType, children }: Props) {

  const currentCountry = useSelector(selectCountry)
  const frontlines = useSelector(selectFrontlineCountries)
  const domainCountry = useSelector(selectDomainCountry)
  const { classes } = useStyles()  
  const t = useTranslation()
  const [sendingState, setSendingState] = useState('not-ready')
  const [formSent, setFormSent] = useState(false)

  const usStateOptions = usStates.map(item => {
    return {
      label: t(`state-${item.value}`),
      value: item.value
    }
  })

  const provinceTerritoryOptions = caTerritories.map(item => {
    return {
      label: t(`province-territory-${item.value}`),
      value: item.value
    }

  })

  const countryOptions =sortCountries(countryList,t).map((item:any) => {
      return {
        value: item.name,
        label: t('country-'+item.alpha3)
      }
    })

  const frontlineCountryOptions = sortCountries(frontlines,t).map((item:any) => {
      return {
        value: item.name,
        label: t('country-'+item.alpha3)
      }
    })

  const debounced = useDebouncedCallback( (target:any, cTarget:any, key:string) => {
    target.currentTarget =  cTarget
    form.getInputProps(key as keyof typeof form.values).onChange(target)
  }, 300)

  const form = useForm({
    initialValues: {
      help: 'quote',
      helpMessage: '',
      business: '',
      country: '',
      projectCity: '',
      projectName: '',
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
      company: '',
      address: '',
      location: currentCountry ?currentCountry :'',
      state: '',
      city: '',
      zipCode: '',

      website:'',
      url:'',
      homepage:''
    },
    validate: {
      email: (value) => (/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(value) ? null : t('ui-form-invalid-email')),
      // email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
      phone: (value) => {
        var re = /^[+][0-9]{1,3}[-\s.]?[(]?[0-9]{1,3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im
        if( re.test(String(value).toLowerCase()) ) {
          return null 
        } else {
          return t('ui-form-invalid-phone-number')
        }
      },
  
    },
  });

  const showFormValues = () => {
    console.log({values:form.values})
  }

  const sendContactForm = async (values:any) => {
    setSendingState('sending')
    showFormValues()
    const { recaptchaToken, recaptchaNumber } = await executeRecaptcha('download_kone_com')
    // setFormSent(true)
    // console.log(recaptchaToken, recaptchaNumber)
    sendFormGeneric(store, {...values, recaptchaToken, recaptchaNumber}).then( result => {
      if(result.status !== 'Error') {
        setSendingState('ready')
      } else {
        setSendingState('error')
        // what now
      }
    })

  }

  const handleFormOnChange = (event:any, key:string) => {
    if (event.currentTarget === document.activeElement) 
    {
      debounced(event, event.currentTarget, key)
    } else {
      form.getInputProps(key as keyof typeof form.values).onChange(event)
    } 
  }
// console.log({formValues})  
  // console.log({f:form.getInputProps('lastName')})
  return (
    (sendingState !== 'ready' 
      ? (
        <>
          <LoadingOverlay visible={sendingState === 'sending'} style={{top:0, position:'fixed'}} />
          <form onSubmit={form.onSubmit((values) => {
            // TODO: Do real implementation
            sendContactForm(values)
          })}>
            <Title order={2}>{t('ui-planner-contact-kone')}</Title>
            <Space h="xs" />
            <Divider my="sm" />
            { sendingState === 'error' &&
                <Alert icon={<InfoCircleRed />} color="red" mb={16}>
                  {t('ui-alert-failed-to-send-form-general')}
                </Alert>
            }
            <Space h="xs" />
            <Title order={3}>{t('ui-planner-contact-ingress')}</Title>
            <Space h="xl" />
            <Group direction="column" align="stretch" className={styles.ContactForm} pb={56} >
              <RadioGroup
                orientation="vertical"
                label={t('ui-planner-form-business-type-header-'+contactType)}
                required
                {...form.getInputProps('business')}
              >
                <Radio value="NEB" label={t('ui-form-new-building')} />
                <Radio value="MOD" label={t('ui-form-existing-building')} />
              </RadioGroup>
              <Textarea
                label={t('ui-form-how-help')}
                placeholder={t('ui-form-additional-details')}
                // {...form.getInputProps('helpMessage')}
                onChange={event=>handleFormOnChange(event, 'helpMessage')}
              />

              <Title order={2}>{t('ui-form-project-info')}</Title>
              <Select
                label={t('ui-form-country-of-project')}
                placeholder={t('ui-form-select')}
                rightSection={<RightSection />}
                required
                searchable
                data={frontlineCountryOptions ?frontlineCountryOptions :[]}
                {...form.getInputProps('country')}
              />
              <TextInput label={t('ui-form-city')}
              onChange={event=>handleFormOnChange(event, 'city')}
                //  {...form.getInputProps('projectCity')}
              />
              {form.getInputProps('country').value === 'United States' && 
                <Select
                  label={t('ui-form-state')}
                  placeholder={t('ui-form-select')}
                  rightSection={<RightSection />}
                  data={usStateOptions}
                  searchable
                  {...form.getInputProps('state')}
                />
              }
              {form.getInputProps('country').value === 'Canada' && 
                <Select
                  label={t('ui-form-province-or-territory')}
                  placeholder={t('ui-form-select')}
                  rightSection={<RightSection />}
                  data={provinceTerritoryOptions}
                  searchable
                  {...form.getInputProps('state')}
                />
              }
              <TextInput label={
                form.getInputProps('country').value === 'United States'
                  ?t('ui-form-zip-code')
                  :t('ui-form-postal-code')
                  } required
                  onChange={event=>handleFormOnChange(event,'zipCode')}
                  error={form.getInputProps('zipCode').error}
                  // {...form.getInputProps('zipCode')}
                />         
              <Title order={2}>{t('ui-form-your-details')}</Title>
              <Select
                label={t('ui-form-title')}
                placeholder={t('ui-form-select')}
                rightSection={<RightSection />}
                data={titles.map( item => { return {value:item.value, label:t(item.text)} })}
              />
              <Select
                label={t('ui-form-your-role-plain')}
                placeholder={t('ui-form-select')}
                rightSection={<RightSection />}
                required
                data={roles.map( item => { return {value:item.id, label:t(item.name)} })}
              />
              <TextInput label={t('ui-form-first-name')} required 
              onChange={event=>handleFormOnChange(event, 'firstName')}
              error={form.getInputProps('firstName').error}
              //  {...form.getInputProps('firstName')}
              />
              <TextInput label={t('ui-form-last-name')} required
              onChange={event=>handleFormOnChange(event,'lastName') }
              error={form.getInputProps('lastName').error}
              //  {...form.getInputProps('lastName')}
              />
              <TextInput label={t('ui-form-email')} required /* {...form.getInputProps('email')} */
                onChange={event=>handleFormOnChange(event, 'email')}
                error={form.getInputProps('email').error}
                />
              <TextInput label={t('ui-form-phone')} required
                onChange={event=>handleFormOnChange(event, 'phone')}
                error={form.getInputProps('phone').error}
                // {...form.getInputProps('phone')}
                placeholder='+xxxxxxxxxxx'
              />
              <TextInput label={t('ui-form-company')} required
                onChange={event=>handleFormOnChange(event, 'company')}
                // {...form.getInputProps('company')}
              />

              <TextInput label="Website" style={{display: 'none'}} tabIndex={-1}
                {...form.getInputProps('website')}
              />
              <TextInput label="URL" style={{position: 'absolute', left:'-9999px'}} tabIndex={-1}
                {...form.getInputProps('url')}
              />
              <TextInput label="Homepage" style={{opacity:0, position:'absolute', zIndex:'-1'}} tabIndex={-1}
                {...form.getInputProps('homepage')}
              />

              <Select
                label={t('ui-form-country')}
                placeholder={t('ui-form-select')}
                rightSection={<RightSection />}
                data={countryOptions}
                searchable
                required
                {...form.getInputProps('location')}
              />

              <Text size="sm">
                {t('ui-contact-privacy-statement-1')}
                  <a
                    tabIndex={-1}
                    className="readMore"
                    href={domainCountry?.privacyStatement}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    &nbsp;
                    {t('ui-contact-privacy-statement-2')}
                  </a>
                {t('ui-contact-privacy-statement-3')}
              </Text>
              <Space h="xl" />

              <Button type="submit" size="lg" className={classes.formButtonContactForm}>{t("ui-general-send")}</Button>
              
              <Space h="xl" />

              <Text size='md'>{t('ui-planner-form-response-notice')}</Text>
            </Group>
          </form>  
        </>
      )
      : <>
          <Title order={2} align="center">{t('cad-planner-contact-thank-you')}</Title>
          <Divider my="sm" />
          <Space h="xl" />
          <Text align="center" size='lg'>{t('ui-planner-form-response-notice')}</Text>
        </>
    )
  )
}

export default ContactFormGeneric;

function RightSection() {
  const ref = useRef<HTMLImageElement>(null)
  useEffect(() => {
    const element = ref.current
    if (element && element.parentElement) {
      element.parentElement.style.pointerEvents = "none"
    }
  }, [])

  return (
    <img
      ref={ref}
      alt="chevron down"
      width={18}
      height={18}
      src={chevronDown}
      style={{ pointerEvents: 'none' }}
    />
  )
}